import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  createCheckoutSession, 
  redirectToCheckout, 
  PLAN_TYPES, 
  BILLING_PERIODS,
  type PlanType,
  type BillingPeriod 
} from '../services/stripeService';
import { CURRENT_APP_PRICING } from '../services/stripeService';

interface StripeCheckoutProps {
  planType: PlanType;
  billingPeriod: BillingPeriod;
  userId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  planType,
  billingPeriod,
  userId,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      
      // 1. 체크아웃 세션 생성
      const { sessionId } = await createCheckoutSession(planType, billingPeriod, userId);
      
      // 2. Stripe 체크아웃 페이지로 리다이렉트
      await redirectToCheckout(sessionId);
      
      // 3. 성공 콜백 호출
      onSuccess?.();
      
    } catch (error) {
      console.error('Subscription error:', error);
      alert(
        t('paywall.subscribe', '구독하기')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // 가격 표시 (USD)
  const getPriceDisplay = () => {
    const amount = CURRENT_APP_PRICING[planType.toUpperCase() as keyof typeof CURRENT_APP_PRICING][billingPeriod];
    const dollars = amount / 100;
    return `US$${dollars.toFixed(2)}`;
  };

  // 플랜 이름 표시
  const getPlanDisplayName = () => {
    switch (planType) {
      case PLAN_TYPES.CARD_ONLY:
        return '기본';
      case PLAN_TYPES.STANDARD:
        return '스탠다드';
      case PLAN_TYPES.PRO:
        return '프로';
      default:
        return planType;
    }
  };

  // 결제 기간 표시
  const getBillingPeriodDisplay = () => {
    return billingPeriod === BILLING_PERIODS.MONTHLY 
      ? '월간'
      : '연간';
  };

  return (
    <div className="stripe-checkout-card">
      <div className="card-content">
        <h3 className="plan-name">
          {getPlanDisplayName()}
        </h3>
        
        <div className="price">
          {getPriceDisplay()}
        </div>
        
        <div className="billing-period">
          {getBillingPeriodDisplay()}
        </div>
        
        <div className="features">
          {/* 플랜별 기능 표시 */}
          {planType === PLAN_TYPES.CARD_ONLY && (
            <ul>
              <li>✓ 2 Cards</li>
              <li>✓ Unlimited QR Generation</li>
              <li>✓ QR Logo Addition</li>
            </ul>
          )}
          {planType === PLAN_TYPES.STANDARD && (
            <ul>
              <li>✓ 5 Cards</li>
              <li>✓ 8 Invoices per Month</li>
              <li>✓ Unlimited QR Generation</li>
              <li>✓ QR Logo Addition</li>
              <li>✓ Special Settings per Card</li>
              <li>✓ Basic Statistics</li>
            </ul>
          )}
          {planType === PLAN_TYPES.PRO && (
            <ul>
              <li>✓ Unlimited Cards</li>
              <li>✓ Unlimited Invoices</li>
              <li>✓ Custom Domain</li>
              <li>✓ Unlimited QR Generation</li>
              <li>✓ Advanced Analytics</li>
              <li>✓ Priority Support</li>
            </ul>
          )}
        </div>
        
        <div className="actions">
          <button 
            className="subscribe-btn"
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? t('common.loading', '처리 중...') : t('paywall.subscribe', '구독하기')}
          </button>
          
          <button 
            className="cancel-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            {t('common.cancel', '취소')}
          </button>
        </div>
      </div>
      
      {/* 인라인 스타일로 변경 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .stripe-checkout-card {
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            padding: 24px;
            margin: 16px 0;
            border: 2px solid #e5e7eb;
          }
          
          .card-content {
            text-align: center;
          }
          
          .plan-name {
            font-size: 24px;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 16px;
          }
          
          .price {
            font-size: 36px;
            font-weight: 700;
            color: #059669;
            margin-bottom: 8px;
          }
          
          .billing-period {
            font-size: 16px;
            color: #6b7280;
            margin-bottom: 24px;
          }
          
          .features {
            margin-bottom: 24px;
          }
          
          .features ul {
            list-style: none;
            padding: 0;
            text-align: left;
          }
          
          .features li {
            padding: 8px 0;
            color: #374151;
            font-size: 14px;
          }
          
          .actions {
            display: flex;
            gap: 12px;
            justify-content: center;
          }
          
          .subscribe-btn {
            background: #059669;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          
          .subscribe-btn:hover:not(:disabled) {
            background: #047857;
          }
          
          .subscribe-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
          }
          
          .cancel-btn {
            background: #6b7280;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          
          .cancel-btn:hover {
            background: #4b5563;
          }
        `
      }} />
    </div>
  );
};
